/**
 * Traçabilité d'une vente : de quel lot est sortie chaque ligne, et de quelle
 * livraison venait ce lot.
 *
 * Mesuré sur 90 jours et 41 338 sorties de vente : 100 % ont un lot, 94 %
 * remontent à une livraison, 76 % à son document archivé. Les deux maillons
 * faibles sont la photo d'étiquette (9 %) et le numéro de lot FOURNISSEUR
 * (0,4 %) — tous deux saisis à la réception.
 *
 * Ce bloc les MONTRE en clair plutôt que de laisser une case vide : « non
 * saisi » dit où la réception doit s'améliorer, avant qu'un contrôle ne le
 * demande. Une case vide ne dit rien, et on finit par croire que la donnée
 * n'existe pas.
 */

import { useEffect, useState } from 'react';
import { api } from '../../services/api';
import { logger } from '../../utils/logger';
import { formatQty } from '../../utils/pos.utils';

interface LigneTracabilite {
  product_name: string;
  quantite: number;
  lot_number: string | null;
  supplier_lot_number: string | null;
  supplier_name: string | null;
  expiry_date: string | null;
  reception_date: string | null;
  label_photo_url: string | null;
  livraison: {
    type: string;
    numero: string;
    date: string | null;
    document_url: string | null;
    fournisseur: string | null;
  } | null;
}

const jour = (v: string | null) =>
  v ? new Date(v).toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit', year: '2-digit' }) : null;

export default function TracabiliteVente({ saleId }: { saleId: string }) {
  const [lignes, setLignes] = useState<LigneTracabilite[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const r = await api.getSaleTraceability(saleId);
        if (!annule) setLignes(r);
      } catch (e: any) {
        logger.error('[Tracabilite] chargement echoue:', e);
        if (!annule) setErreur('Traçabilité indisponible pour cette vente.');
      }
    })();
    return () => {
      annule = true;
    };
  }, [saleId]);

  if (erreur) return <p className="text-sm text-red-700 px-4 py-3">{erreur}</p>;
  if (!lignes) return <p className="text-sm text-gray-500 px-4 py-3">Chargement…</p>;
  if (lignes.length === 0) {
    return (
      <p className="text-sm text-gray-500 px-4 py-3">
        Aucun mouvement de stock rattaché — vente sans déduction (consigne, bon cadeau)
        ou antérieure au suivi par lot.
      </p>
    );
  }

  return (
    <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
        Traçabilité — lot d'origine de chaque article
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b border-gray-200">
              <th className="py-1.5 pr-3 font-medium">Article</th>
              <th className="py-1.5 pr-3 font-medium text-right">Qté</th>
              <th className="py-1.5 pr-3 font-medium">Lot</th>
              <th className="py-1.5 pr-3 font-medium">DLC</th>
              <th className="py-1.5 pr-3 font-medium">Fournisseur</th>
              <th className="py-1.5 pr-3 font-medium">Livraison</th>
              <th className="py-1.5 font-medium">Pièces</th>
            </tr>
          </thead>
          <tbody>
            {lignes.map((l, i) => (
              <tr key={i} className="border-b border-gray-100 last:border-0 align-top">
                <td className="py-1.5 pr-3 font-medium text-gray-900">{l.product_name || '—'}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{formatQty(l.quantite)}</td>
                <td className="py-1.5 pr-3">
                  <span className="font-mono text-xs">{l.lot_number || '—'}</span>
                  {l.reception_date && (
                    <span className="block text-xs text-gray-500">reçu {jour(l.reception_date)}</span>
                  )}
                  <span className="block text-xs">
                    {l.supplier_lot_number ? (
                      <span className="text-gray-700">n° fourn. {l.supplier_lot_number}</span>
                    ) : (
                      <span className="text-amber-700">n° fournisseur non saisi</span>
                    )}
                  </span>
                </td>
                <td className="py-1.5 pr-3">{jour(l.expiry_date) || <span className="text-gray-400">—</span>}</td>
                <td className="py-1.5 pr-3">{l.supplier_name || <span className="text-gray-400">—</span>}</td>
                <td className="py-1.5 pr-3">
                  {l.livraison ? (
                    <>
                      <span className="block">
                        {l.livraison.type} {l.livraison.numero}
                      </span>
                      {l.livraison.date && (
                        <span className="block text-xs text-gray-500">{jour(l.livraison.date)}</span>
                      )}
                    </>
                  ) : (
                    <span className="text-amber-700 text-xs">non reliée</span>
                  )}
                </td>
                <td className="py-1.5 space-x-2 whitespace-nowrap">
                  {l.livraison?.document_url && (
                    <a href={l.livraison.document_url} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline text-xs">
                      Bon
                    </a>
                  )}
                  {l.label_photo_url && (
                    <a href={l.label_photo_url} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline text-xs">
                      Étiquette
                    </a>
                  )}
                  {!l.livraison?.document_url && !l.label_photo_url && (
                    <span className="text-gray-400 text-xs">aucune</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
